/** The platform's prohibited and export-control (dual-use) lists (CAT-5). */
export const RISK_LIST_TYPES = ["prohibited", "export_control"];
/** An entry names an HS-code prefix (goods only), a keyword in any language, or a category and all below it. */
export const RISK_MATCH_TYPES = ["hs_prefix", "keyword", "category"];
const HS_PREFIX = /^\d{2,10}$/;
/** Scripts written without spaces between words, where a keyword matches anywhere. */
const UNSPACED = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}\p{Script=Thai}]/u;
const WORD = /[\p{L}\p{N}]/u;
/** Keywords and texts compared case-insensitively, with full-width forms folded and spaces collapsed. */
export function normalizeKeyword(text) {
    return text.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
/** Whether an operator's entry is well formed; that a category entry names a category is the caller's check. */
export function riskEntryIssues(matchType, value) {
    switch (matchType) {
        case "hs_prefix":
            return HS_PREFIX.test(value) ? [] : [{ path: "value", code: "INVALID_HS_PREFIX" }];
        case "keyword": {
            const keyword = normalizeKeyword(value);
            if (keyword.length < 2)
                return [{ path: "value", code: "TOO_SHORT" }];
            return keyword.length > 100 ? [{ path: "value", code: "TOO_LONG" }] : [];
        }
        case "category":
            return UUID.test(value) ? [] : [{ path: "value", code: "NOT_A_CATEGORY" }];
    }
}
/**
 * A keyword in a script with spaces matches whole words only, so "arms" does not match "farms"; in Chinese or Japanese
 * it matches anywhere in the text.
 */
function containsKeyword(text, keyword) {
    if (UNSPACED.test(keyword))
        return text.includes(keyword);
    for (let at = text.indexOf(keyword); at !== -1; at = text.indexOf(keyword, at + 1)) {
        const before = at === 0 ? "" : (text[at - 1] ?? "");
        const after = text[at + keyword.length] ?? "";
        if (!WORD.test(before) && !WORD.test(after))
            return true;
    }
    return false;
}
/** The entries a listing matches; any match sends it to an operator before it goes live (CAT-5). */
export function riskMatches(subject, entries) {
    const texts = subject.texts.map(normalizeKeyword).filter((text) => text !== "");
    const categories = new Set(subject.categoryIds);
    const matches = (entry) => {
        switch (entry.matchType) {
            case "hs_prefix":
                return subject.kind === "goods" && subject.hsCode !== null && subject.hsCode.startsWith(entry.value);
            case "keyword": {
                const keyword = normalizeKeyword(entry.value);
                return keyword !== "" && texts.some((text) => containsKeyword(text, keyword));
            }
            case "category":
                return categories.has(entry.value);
        }
    };
    return entries.filter(matches);
}
/** Every text a listing shows: name, description, text attributes, SKU codes and options, and image alt text. */
export function listingTexts(listing) {
    const localized = (text) => text === null ? [] : [text.en, ...(text.zh === undefined ? [] : [text.zh])];
    return [
        ...localized(listing.name),
        ...localized(listing.description),
        ...Object.values(listing.attributes).filter((value) => typeof value === "string"),
        ...listing.skus.flatMap((sku) => [sku.code, ...Object.values(sku.options)]),
        ...listing.media.flatMap((item) => localized(item.alt)),
    ];
}
//# sourceMappingURL=risk-lists.js.map